"use client";

import { c as _c } from "react/compiler-runtime";
import { UserFollowBtnSize } from "@/components/user/utils/UserFollowBtn";
import type { DropInteractionParams } from "@/components/waves/drops/Drop";
import { useEmoji } from "@/contexts/EmojiContext";
import { numberWithCommas } from "@/helpers/Helpers";
import type { ExtendedDrop } from "@/helpers/waves/drop.helpers";
import type { ActiveDropState } from "@/types/dropInteractionTypes";
import type { INotificationDropReacted, INotificationDropVoted } from "@/types/feed.types";
import NotificationsFollowBtn from "../NotificationsFollowBtn";
import NotificationDrop from "../subcomponents/NotificationDrop";
import NotificationHeader from "../subcomponents/NotificationHeader";
import NotificationTimestamp from "../subcomponents/NotificationTimestamp";
import { getIsDirectMessage, useWaveNavigation } from "../utils/navigationUtils";
export const getNotificationVoteColor = (vote: number) => {
  if (vote > 0) return "tw-text-green";
  if (vote < 0) return "tw-text-red";
  return "tw-text-iron-500";
};
type NotificationUnion = INotificationDropVoted | INotificationDropReacted;
interface Props {
  readonly notification: NotificationUnion;
  readonly activeDrop: ActiveDropState | null;
  readonly onReply: (param: DropInteractionParams) => void;
  readonly onQuote: (param: DropInteractionParams) => void;
  readonly onDropContentClick?: ((drop: ExtendedDrop) => void) | undefined;
}
export default function NotificationDropReacted(t0) {
  const $ = _c(62);
  const {
    notification,
    activeDrop,
    onReply,
    onQuote,
    onDropContentClick
  } = t0;
  const {
    createReplyClickHandler,
    createQuoteClickHandler
  } = useWaveNavigation();
  const {
    findCustomEmoji,
    findNativeEmoji
  } = useEmoji();
  const isVoted = (notification as INotificationDropVoted).additional_context.vote !== undefined;
  const isReacted = (notification as INotificationDropReacted).additional_context.reaction !== undefined;
  let actionElement;
  if (isVoted) {
    const voteValue = (notification as INotificationDropVoted).additional_context.vote;
    let t1;
    if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
      t1 = <span className="tw-text-iron-400 tw-font-normal tw-text-sm">rated</span>;
      $[0] = t1;
    } else {
      t1 = $[0];
    }
    let t2;
    if ($[1] !== voteValue) {
      t2 = getNotificationVoteColor(voteValue);
      $[1] = voteValue;
      $[2] = t2;
    } else {
      t2 = $[2];
    }
    const t3 = `${t2} tw-font-medium tw-text-sm`;
    const t4 = voteValue > 0 && "+";
    let t5;
    if ($[3] !== voteValue) {
      t5 = numberWithCommas(voteValue);
      $[3] = voteValue;
      $[4] = t5;
    } else {
      t5 = $[4];
    }
    let t6;
    if ($[5] !== t3 || $[6] !== t4 || $[7] !== t5) {
      t6 = <span className={t3}>{t4}{t5}</span>;
      $[5] = t3;
      $[6] = t4;
      $[7] = t5;
      $[8] = t6;
    } else {
      t6 = $[8];
    }
    let t7;
    if ($[9] !== notification.created_at) {
      t7 = <NotificationTimestamp createdAt={notification.created_at} />;
      $[9] = notification.created_at;
      $[10] = t7;
    } else {
      t7 = $[10];
    }
    let t8;
    if ($[11] !== t6 || $[12] !== t7) {
      t8 = <>{t1}{t6}{t7}</>;
      $[11] = t6;
      $[12] = t7;
      $[13] = t8;
    } else {
      t8 = $[13];
    }
    actionElement = t8;
  } else {
    if (isReacted) {
      const t1 = notification as INotificationDropReacted;
      let emojiNode;
      if ($[14] !== findCustomEmoji || $[15] !== findNativeEmoji || $[16] !== t1.additional_context.reaction) {
        const rawId = t1.additional_context.reaction.replaceAll(":", "");
        emojiNode = null;
        const custom = findCustomEmoji(rawId);
        if (custom) {
          emojiNode = <img src={custom.skins[0]?.src} alt={rawId} className="tw-max-w-5 tw-max-h-5 tw-object-contain" />;
        } else {
          const native = findNativeEmoji(rawId);
          if (native) {
            const t2 = native.skins[0]?.native;
            let t3;
            if ($[18] !== t2) {
              t3 = <span className="tw-text-[1.2rem] tw-flex tw-items-center tw-justify-center">{t2}</span>;
              $[18] = t2;
              $[19] = t3;
            } else {
              t3 = $[19];
            }
            emojiNode = t3;
          }
        }
        $[14] = findCustomEmoji;
        $[15] = findNativeEmoji;
        $[16] = t1.additional_context.reaction;
        $[17] = emojiNode;
      } else {
        emojiNode = $[17];
      }
      if (!emojiNode) {
        return null;
      }
      let t2;
      if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
        t2 = <span className="tw-text-iron-400 tw-font-normal tw-text-sm">reacted</span>;
        $[20] = t2;
      } else {
        t2 = $[20];
      }
      let t3;
      if ($[21] !== notification.created_at) {
        t3 = <NotificationTimestamp createdAt={notification.created_at} />;
        $[21] = notification.created_at;
        $[22] = t3;
      } else {
        t3 = $[22];
      }
      let t4;
      if ($[23] !== emojiNode || $[24] !== t3) {
        t4 = <>{t2}{emojiNode}{t3}</>;
        $[23] = emojiNode;
        $[24] = t3;
        $[25] = t4;
      } else {
        t4 = $[25];
      }
      actionElement = t4;
    } else {
      return null;
    }
  }
  const drop = notification.related_drops[0];
  if (!drop) {
    return null;
  }
  let T0;
  let t1;
  let t2;
  let t3;
  let t4;
  let t5;
  let t6;
  let t7;
  let t8;
  if ($[26] !== actionElement || $[27] !== activeDrop || $[28] !== createQuoteClickHandler || $[29] !== createReplyClickHandler || $[30] !== drop || $[31] !== notification.related_identity || $[32] !== onQuote || $[33] !== onReply) {
    const isDirectMessage = getIsDirectMessage(drop.wave);
    t7 = "tw-w-full tw-flex tw-flex-col tw-space-y-2";
    let t9;
    if ($[43] !== notification.related_identity) {
      t9 = <NotificationsFollowBtn profile={notification.related_identity} size={UserFollowBtnSize.SMALL} />;
      $[43] = notification.related_identity;
      $[44] = t9;
    } else {
      t9 = $[44];
    }
    if ($[45] !== actionElement || $[46] !== notification.related_identity || $[47] !== t9) {
      t8 = <NotificationHeader author={notification.related_identity} actions={t9}>{actionElement}</NotificationHeader>;
      $[45] = actionElement;
      $[46] = notification.related_identity;
      $[47] = t9;
      $[48] = t8;
    } else {
      t8 = $[48];
    }
    T0 = NotificationDrop;
    t1 = drop;
    t2 = activeDrop;
    t3 = onReply;
    t4 = onQuote;
    t5 = createReplyClickHandler(drop.wave.id, isDirectMessage);
    t6 = createQuoteClickHandler(isDirectMessage);
    $[26] = actionElement;
    $[27] = activeDrop;
    $[28] = createQuoteClickHandler;
    $[29] = createReplyClickHandler;
    $[30] = drop;
    $[31] = notification.related_identity;
    $[32] = onQuote;
    $[33] = onReply;
    $[34] = T0;
    $[35] = t1;
    $[36] = t2;
    $[37] = t3;
    $[38] = t4;
    $[39] = t5;
    $[40] = t6;
    $[41] = t7;
    $[42] = t8;
  } else {
    T0 = $[34];
    t1 = $[35];
    t2 = $[36];
    t3 = $[37];
    t4 = $[38];
    t5 = $[39];
    t6 = $[40];
    t7 = $[41];
    t8 = $[42];
  }
  let t9;
  if ($[49] !== T0 || $[50] !== onDropContentClick || $[51] !== t1 || $[52] !== t2 || $[53] !== t3 || $[54] !== t4 || $[55] !== t5 || $[56] !== t6) {
    t9 = <T0 drop={t1} activeDrop={t2} onReply={t3} onQuote={t4} onReplyClick={t5} onQuoteClick={t6} onDropContentClick={onDropContentClick} />;
    $[49] = T0;
    $[50] = onDropContentClick;
    $[51] = t1;
    $[52] = t2;
    $[53] = t3;
    $[54] = t4;
    $[55] = t5;
    $[56] = t6;
    $[57] = t9;
  } else {
    t9 = $[57];
  }
  let t10;
  if ($[58] !== t7 || $[59] !== t8 || $[60] !== t9) {
    t10 = <div className={t7}>{t8}{t9}</div>;
    $[58] = t7;
    $[59] = t8;
    $[60] = t9;
    $[61] = t10;
  } else {
    t10 = $[61];
  }
  return t10;
}